import React from 'react';
import { CheckIcon, LoaderIcon, MinusIcon } from 'lucide-react';
import { CarliStars } from './CarliStars';
import { AdaptationState, FormatId, Placement, formats } from '../data/adStudio';
const stateStyles: Record<AdaptationState, string> = {
  Approved: 'border-positive/35 bg-positive/10 text-positive',
  Ready: 'border-[#BB5E40]/35 bg-[#BB5E40]/8 text-[#94452E]',
  Generating: 'border-[#C9A04E]/40 bg-[#C9A04E]/12 text-[#8B6A20]',
  Missing: 'border-dashed border-ink/18 bg-cream/50 text-ink/40'
};
export function AdStudioMatrix({
  placements,
  onGenerateMissing
}: {
  placements: Placement[];
  onGenerateMissing: () => void;
}) {
  const missing = placements.reduce((sum, placement) => sum + Object.values(placement.formats).filter(state => state === 'Missing').length, 0);
  return <section className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="adapt-heading" data-id="element-191" data-mp="2k8q46iwdbbpk">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-192" data-mp="2f9wr2rkztmpx">
        <div data-id="element-193" data-mp="69jaka3r3ghe">
          <h3 id="adapt-heading" className="text-base font-semibold" data-id="element-194" data-mp="6a2qjl9ubuss">Adapt to all formats</h3>
          <p className="mt-1 text-sm text-ink/60" data-id="element-195" data-mp="b8dz7soefz9f">
            {missing > 0 ? `${missing} placements still need an asset. Carli will crop, recompose, and re-render from the approved concept.` : 'Every placement has an asset. Approved crops are locked.'}
          </p>
        </div>
        <button type="button" onClick={onGenerateMissing} disabled={missing === 0} className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-[#BB5E40] px-4 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#A34E33] disabled:cursor-not-allowed disabled:bg-ink/15 disabled:text-ink/40" data-id="element-196" data-mp="3jr23o90yddon">
          <CarliStars className="h-3.5 w-3.5" color="currentColor" data-id="element-197" data-mp="30xy64ckm6gor" />
          Generate missing
        </button>
      </div>

      <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-198" data-mp="34z1lyz2nwcpz">
        <table className="w-full min-w-[640px] border-separate border-spacing-y-2 text-left text-xs" data-id="element-199" data-mp="c5kt01ip9cex">
          <thead className="text-ink/50" data-id="element-200" data-mp="77j95edg992e">
            <tr data-id="element-201" data-mp="76rgabb8lpft">
              <th scope="col" className="pb-1 pl-1 font-semibold" data-id="element-202" data-mp="756wngqgnqwq">Placement</th>
              {formats.map(format => <th key={format.id} scope="col" className="pb-1 text-center font-semibold" data-id="element-203" data-mp="gx2x2yg0rt44">
                  <span className="block text-ink/70" data-id="element-204" data-mp="3pcmhr92mk37k">{format.id}</span>
                  <span className="block text-[10px] font-normal text-ink/40" data-id="element-205" data-mp="3peqnlwk33cah">{format.label}</span>
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-206" data-mp="28pxlrhq3s4v">
            {placements.map(placement => <tr key={placement.channel} data-id="element-207" data-mp="3ubza29c9m2hj">
                <th scope="row" className="rounded-l-xl bg-cream/45 py-2.5 pl-3 pr-4 font-semibold text-ink" data-id="element-208" data-mp="3ua5ziiyj6y49">
                  <span className="inline-flex items-center gap-2" data-id="element-209" data-mp="1dlaaj4utz173">
                    {placement.logo ? <span aria-hidden="true" className="h-4 w-4 shrink-0" style={{
                  backgroundColor: '#94452E',
                  WebkitMaskImage: `url(${placement.logo})`,
                  maskImage: `url(${placement.logo})`,
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain'
                }} data-id="element-210" data-mp="19nkw9odmhac9" /> : <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-[#94452E] text-[9px] font-bold text-cream" data-id="element-211" data-mp="1v4ad8wg5v8un">
                        {placement.channel.charAt(0)}
                      </span>}
                    {placement.channel}
                  </span>
                </th>
                {formats.map((format, index) => {
              const state = placement.formats[format.id as FormatId] ?? 'Missing';
              return <td key={format.id} className={`bg-cream/45 py-2.5 text-center ${index === formats.length - 1 ? 'rounded-r-xl pr-3' : ''}`} data-id="element-212" data-mp="py2eilg0ako2" data-mpi={index}>
                      <StateChip state={state} data-id="element-213" data-mp="1inj3f94f3jfj" />
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-ink/55" data-id="element-214" data-mp="18wy5lnwkiixo">
        {(['Approved', 'Ready', 'Generating', 'Missing'] as AdaptationState[]).map(state => <span key={state} className="inline-flex items-center gap-1.5" data-id="element-215" data-mp="13wismxg77dg8">
            <span className={`h-2.5 w-2.5 rounded-full border ${stateStyles[state]}`} data-id="element-216" data-mp="21f23p8724jdv" />
            {state}
          </span>)}
      </div>
    </section>;
}
function StateChip({
  state
}: {
  state: AdaptationState;
}) {
  return <span className={`inline-flex min-w-[92px] items-center justify-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold ${stateStyles[state]}`} data-id="element-217" data-mp="28x0ah4xutgde">
      {state === 'Approved' && <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-218" data-mp="2ivpub39b9awa" />}
      {state === 'Generating' && <LoaderIcon className="h-3 w-3 animate-spin" aria-hidden="true" data-id="element-219" data-mp="2nuslpkwdbils" />}
      {state === 'Missing' && <MinusIcon className="h-3 w-3" aria-hidden="true" data-id="element-220" data-mp="2rvw5ujlg80st" />}
      {state}
    </span>;
}